import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Account = () => {
  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
    loadAppointments();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setProfile(response.data.user);
      setFormData({
        firstName: response.data.user.firstName || '',
        lastName: response.data.user.lastName || '',
        phone: response.data.user.phone || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const response = await api.get('/user/dashboard');
      const allAppointments = [
        ...(response.data.upcoming || []),
        ...(response.data.past || [])
      ];
      setAppointments(allAppointments);
    } catch (error) {
      console.error('Failed to load appointments:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put('/user/profile', formData);
      setProfile(response.data.user);
      setEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update profile'
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    try {
      await api.put('/user/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setChangingPassword(false);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to change password'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/logout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tertiary flex items-center justify-center">
        <div className="text-primary text-xl">Loading your account...</div>
      </div>
    );
  }

  const menuItems = [
    { id: 'profile', label: 'Personal Information', icon: '👤' },
    { id: 'appointments', label: 'Appointment History', icon: '📅' },
    { id: 'security', label: 'Password & Security', icon: '🔒' },
    { id: 'payments', label: 'Payment Methods', icon: '💳' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-tertiary via-neutral-warm to-tertiary py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-light text-primary mb-2">
            Account Settings
          </h1>
          <p className="text-primary-light">Manage your account preferences and security</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
            {message.text}
          </div>
        )}

        {/* Main Layout with Sidebar */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left Sidebar Navigation */}
          <div className="md:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <nav className="divide-y divide-gray-100">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setEditing(false);
                      setChangingPassword(false);
                      setMessage({ type: '', text: '' });
                    }}
                    className={`w-full text-left px-6 py-4 flex items-center gap-3 transition-all ${
                      activeTab === item.id
                        ? 'bg-accent text-white font-semibold'
                        : 'text-primary hover:bg-tertiary'
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>

              {/* Sign Out Button */}
              <div className="p-4 border-t-2 border-gray-100">
                <button
                  onClick={handleLogout}
                  className="w-full bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-dark transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Personal Information Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-accent">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-light text-primary">Personal Information</h2>
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="bg-accent text-white px-6 py-2 rounded-full font-semibold hover:bg-accent-dark transition-all"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>

                {editing ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-primary mb-2 font-medium">First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-secondary rounded-lg focus:outline-none focus:border-primary transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-primary mb-2 font-medium">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-secondary rounded-lg focus:outline-none focus:border-primary transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-primary mb-2 font-medium">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-secondary rounded-lg focus:outline-none focus:border-primary transition-colors"
                        placeholder="(555) 555-5555"
                      />
                    </div>

                    <div>
                      <label className="block text-primary mb-2 font-medium">Email</label>
                      <input
                        type="email"
                        value={profile?.email}
                        disabled
                        className="w-full px-4 py-3 border border-secondary rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                      <p className="text-sm text-primary-light mt-1">Email cannot be changed</p>
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="submit"
                        className="bg-accent text-white px-8 py-3 rounded-full font-semibold hover:bg-accent-dark transition-all"
                      >
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(false);
                          setFormData({
                            firstName: profile.firstName,
                            lastName: profile.lastName,
                            phone: profile.phone || ''
                          });
                        }}
                        className="bg-white border-2 border-primary text-primary px-8 py-3 rounded-full font-semibold hover:bg-tertiary transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-primary-light mb-1">First Name</p>
                        <p className="text-lg text-primary font-medium">{profile?.firstName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-primary-light mb-1">Last Name</p>
                        <p className="text-lg text-primary font-medium">{profile?.lastName}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-primary-light mb-1">Email</p>
                      <p className="text-lg text-primary font-medium">{profile?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-primary-light mb-1">Phone Number</p>
                      <p className="text-lg text-primary font-medium">{profile?.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-primary-light mb-1">Member Since</p>
                      <p className="text-lg text-primary font-medium">
                        {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Appointment History Tab */}
            {activeTab === 'appointments' && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-accent">
                <h2 className="text-2xl font-light text-primary mb-6">Appointment History</h2>
                {appointments.length === 0 ? (
                  <div className="bg-tertiary rounded-lg p-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-primary-light mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-xl font-medium text-primary mb-2">No Appointments Yet</h3>
                    <p className="text-primary-light mb-6">You haven't booked any appointments.</p>
                    <button
                      onClick={() => navigate('/')}
                      className="bg-accent text-white px-8 py-3 rounded-full font-semibold hover:bg-accent-dark transition-all"
                    >
                      Book Your First Appointment
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appointments.map((apt) => {
                      const isUpcoming = new Date(apt.appointmentDate) >= new Date();
                      const statusColor =
                        apt.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        apt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800';

                      return (
                        <div
                          key={apt.id}
                          className={`rounded-xl p-6 border-2 transition-all ${
                            isUpcoming ? 'border-accent bg-accent-lightest' : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-2xl font-bold text-primary">
                                  {new Date(apt.appointmentDate).toLocaleDateString('en-US', { day: 'numeric' })}
                                </span>
                                <div>
                                  <div className="text-lg font-medium text-primary">
                                    {new Date(apt.appointmentDate).toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </div>
                                  <div className="text-accent font-semibold">{apt.startTime}</div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                                {apt.serviceName && (
                                  <div>
                                    <p className="text-xs text-primary-light">Service</p>
                                    <p className="text-sm font-medium text-primary">{apt.serviceName}</p>
                                  </div>
                                )}
                                {apt.treatmentArea && (
                                  <div>
                                    <p className="text-xs text-primary-light">Treatment Area</p>
                                    <p className="text-sm font-medium text-primary">{apt.treatmentArea}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs text-primary-light">Duration</p>
                                  <p className="text-sm font-medium text-primary">{apt.durationMinutes} min</p>
                                </div>
                                <div>
                                  <p className="text-xs text-primary-light">Price</p>
                                  <p className="text-sm font-medium text-primary">${apt.price}</p>
                                </div>
                              </div>

                              {apt.notes && (
                                <div className="mt-3 bg-white rounded-lg p-3 border border-gray-200">
                                  <p className="text-xs text-primary-light mb-1">Notes</p>
                                  <p className="text-sm text-primary">{apt.notes}</p>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col items-end gap-2">
                              <span className={`px-4 py-2 rounded-full text-sm font-medium capitalize ${statusColor}`}>
                                {apt.status}
                              </span>
                              {apt.paid && (
                                <span className="px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  ✓ Paid
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Password & Security Tab */}
            {activeTab === 'security' && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-primary">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-light text-primary">Password & Security</h2>
                  {!changingPassword && (
                    <button
                      onClick={() => setChangingPassword(true)}
                      className="bg-primary text-white px-6 py-2 rounded-full font-semibold hover:bg-primary-dark transition-all"
                    >
                      Change Password
                    </button>
                  )}
                </div>

                {changingPassword ? (
                  <form onSubmit={handleChangePassword} className="space-y-6">
                    <div>
                      <label className="block text-primary mb-2 font-medium">Current Password</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-secondary rounded-lg focus:outline-none focus:border-primary transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-primary mb-2 font-medium">New Password</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength="8"
                        className="w-full px-4 py-3 border border-secondary rounded-lg focus:outline-none focus:border-primary transition-colors"
                      />
                      <p className="text-sm text-primary-light mt-1">At least 8 characters</p>
                    </div>

                    <div>
                      <label className="block text-primary mb-2 font-medium">Confirm New Password</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-secondary rounded-lg focus:outline-none focus:border-primary transition-colors"
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="submit"
                        className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary-dark transition-all"
                      >
                        Update Password
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setChangingPassword(false);
                          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        }}
                        className="bg-white border-2 border-primary text-primary px-8 py-3 rounded-full font-semibold hover:bg-tertiary transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <p className="text-primary-light mb-6">
                      Keep your account secure by using a strong password and changing it regularly.
                    </p>
                    <div className="bg-tertiary rounded-lg p-6">
                      <h3 className="font-medium text-primary mb-3">Password Requirements</h3>
                      <ul className="space-y-2 text-sm text-primary-light">
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          At least 8 characters long
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Use a unique password not used elsewhere
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'payments' && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-accent-light">
                <h2 className="text-2xl font-light text-primary mb-6">Payment Methods</h2>
                <div className="bg-tertiary rounded-lg p-12 text-center">
                  <svg className="w-16 h-16 mx-auto text-primary-light mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <h3 className="text-xl font-medium text-primary mb-2">No Payment Methods</h3>
                  <p className="text-primary-light mb-6">You haven't added any payment methods yet.</p>
                  <button
                    disabled
                    className="bg-gray-300 text-gray-500 px-8 py-3 rounded-full font-semibold cursor-not-allowed"
                  >
                    Add Payment Method (Coming Soon)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
